import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useLayoutStore } from '../store/layout'
import type {
  Proposal, ProposalArea, ProposalLineItem, Client, ActivityLog,
} from '@shared/types'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { CatalogPickerModal } from '../components/CatalogPickerModal'
import BrandingOverrideModal from '../components/BrandingOverrideModal'
import { useBrandingStore } from '../store/branding'
import { useToast } from '../components/ui/Toast'

// ─── Types ───

interface ProposalPayment {
  id: string; proposal_id: string; label: string; amount_type: string
  amount_value: number | null; trigger_type: string; trigger_milestone_id: string | null
  trigger_date: string | null; calculated_amount: number | null
  status: string; sort_order: number
}

interface ProposalWithDetails extends Proposal {
  areas: ProposalArea[]
  line_items: ProposalLineItem[]
  payments: ProposalPayment[]
}

// ─── Helpers ───

const fmt = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const _fmtMoney = (n: number | null | undefined) => '$' + fmt(n)

function fmtDate(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function timeAgo(d: string | null | undefined): string {
  if (!d) return ''
  const diff = Date.now() - new Date(d).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return fmtDate(d)
}

const BRAND_COLORS: Record<string, string> = {
  Hikvision: '#cc0000', Ubiquiti: '#2563eb', Dell: '#007db8', LTS: '#1a5276',
  Denon: '#1a1a2e', Sonance: '#2d5a27', 'JL Audio': '#8b0000',
  Sony: '#000000', Wavelynx: '#1e3a5f', 'Orion Access': '#d4880f',
  APC: '#ff6600', Monoprice: '#4b5563', Seagate: '#6fa64d',
}

function getBrandColor(brand?: string): string {
  if (!brand) return '#6b7280'
  return BRAND_COLORS[brand] || `hsl(${brand.charCodeAt(0) * 47 % 360}, 38%, 44%)`
}

const STATUS_MAP: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'var(--accent-m)', text: '#a17c2e', dot: 'var(--accent)' },
  sent: { label: 'Sent', bg: 'var(--info-m)', text: 'var(--info)', dot: 'var(--info)' },
  viewed: { label: 'Viewed', bg: 'rgba(139,92,246,0.08)', text: '#7c3aed', dot: '#7c3aed' },
  approved: { label: 'Approved', bg: 'var(--ok-m)', text: 'var(--ok)', dot: 'var(--ok)' },
  rejected: { label: 'Declined', bg: 'var(--err-m)', text: 'var(--err)', dot: 'var(--err)' },
  expired: { label: 'Expired', bg: 'rgba(107,114,128,0.08)', text: '#6b7280', dot: '#6b7280' },
  lost: { label: 'Lost', bg: 'var(--err-m)', text: 'var(--err)', dot: 'var(--err)' },
  changes_required: { label: 'Changes Requested', bg: 'var(--warn-m)', text: '#b45309', dot: 'var(--warn)' },
}

// ─── Icons ───

const I = {
  back: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>,
  more: <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>,
  chev: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} style={{ color: 'var(--text-tertiary)' }}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>,
  plus: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}><path strokeLinecap="round" d="M12 5v14M5 12h14"/></svg>,
  send: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>,
  eye: <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  copy: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>,
  pdf: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>,
  clone: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="8" y="8" width="14" height="14" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>,
  trash: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
  link: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>,
  edit: <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  archive: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><polyline points="21,8 21,21 3,21 3,8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
  settings: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
  clock: <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
}

// ─── Views enum ───
const V = { MAIN: 0, AREA: 1, FINANCIAL: 2, PROFIT: 3, PAYMENTS: 4, ACTIVITY: 5 } as const

// ─── Main Component ───

export default function ProposalDetailMobile() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const setHideShell = useLayoutStore(s => s.setHideShell)
  const { marginAlertThreshold, marginProposalWarningEnabled } = useBrandingStore()
  const [proposal, setProposal] = useState<ProposalWithDetails | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [activity, setActivity] = useState<ActivityLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [shareUrl, setShareUrl] = useState('')
  const [tab, setTab] = useState(0)
  const [view, setView] = useState<number>(V.MAIN)
  const [activeArea, setActiveArea] = useState<ProposalArea | null>(null)
  const [showActions, setShowActions] = useState(false)
  const [addingArea, setAddingArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')
  const [savingArea, setSavingArea] = useState(false)
  const [catalogAreaId, setCatalogAreaId] = useState<string | null>(null)
  const [_cloning, setCloning] = useState(false)
  const [showBranding, setShowBranding] = useState(false)
  const [vmsRules, setVmsRules] = useState<{ vms_type: string; vms_display_name: string; is_active: number }[]>([])
  const [vmsUpdating, setVmsUpdating] = useState(false)
  const [activeItem, setActiveItem] = useState<ProposalLineItem | null>(null)
  const { toast, toastError } = useToast()

  // ─── Hide app shell on mobile (proposal has its own header) ───
  useEffect(() => {
    setHideShell(true)
    return () => setHideShell(false)
  }, [setHideShell])

  // ─── Data Fetching ───
  useEffect(() => {
    if (!slug) return
    setLoading(true)
    api.get<ProposalWithDetails>(`/proposals/${slug}`)
      .then(async (data) => {
        setProposal(data)
        if (data.client_token) setShareUrl(`${window.location.origin}/p/${data.client_token}`)
        if (data.client_id) {
          try { setClient(await api.get<Client>(`/clients/by-id/${data.client_id}`)) } catch { /* */ }
        }
        try {
          const logs = await api.get<{ data: ActivityLog[] }>(`/activity-log?entity_type=proposal&entity_id=${data.id}&limit=10`)
          setActivity(logs.data ?? [])
        } catch { /* */ }
        try {
          const rules = await api.get<{ vms_type: string; vms_display_name: string; is_active: number }[]>('/vms-license-rules')
          setVmsRules(rules.filter(r => r.is_active))
        } catch { /* */ }
      })
      .catch((err) => setError(err?.message || 'Failed to load'))
      .finally(() => setLoading(false))
  }, [slug])

  // ─── Actions ───
  const go = (v: number, area: ProposalArea | null = null) => {
    setActiveArea(area)
    setView(v)
    setShowActions(false)
  }
  const back = () => { setView(V.MAIN); setActiveArea(null) }

  async function handleStatusChange(newStatus: string) {
    setShowActions(false)
    try {
      const updated = await api.patch<ProposalWithDetails>(`/proposals/${slug}`, { status: newStatus })
      setProposal(prev => prev ? { ...prev, ...updated } : prev)
    } catch { /* */ }
  }

  async function handleVmsTypeChange(vmsType: string | null) {
    setVmsUpdating(true)
    try {
      await api.patch(`/proposals/${slug}`, { vms_type: vmsType })
      const full = await api.get<ProposalWithDetails>(`/proposals/${slug}`)
      setProposal(full)
    } catch { /* */ }
    finally { setVmsUpdating(false) }
  }

  async function generateToken() {
    try {
      const res = await api.post<{ token: string; url: string }>(`/proposals/${slug}/generate-token`)
      setShareUrl(res.url)
      return res.url
    } catch { /* */ }
    return null
  }

  // Prints the client-facing view through the browser's own print-to-PDF.
  // The tab is opened synchronously so it is not blocked as a popup.
  async function downloadPdf() {
    setShowActions(false)
    const tab = window.open('', '_blank')
    if (!tab) {
      toastError('Allow pop-ups for this site to download the PDF')
      return
    }
    try {
      const url = shareUrl || await generateToken()
      if (!url) throw new Error('Could not prepare the proposal document')
      tab.location.href = `${url}${url.includes('?') ? '&' : '?'}print=1`
      toast('Opening your print dialog — choose "Save as PDF"', 'info')
    } catch (err) {
      tab.close()
      toastError('Failed to prepare the PDF', err)
    }
  }

  async function handleClone() {
    setCloning(true)
    setShowActions(false)
    try {
      const cloned = await api.post<Proposal>(`/proposals/${slug}/clone`)
      window.location.href = `/proposals/${cloned.slug}`
    } catch { /* */ }
    finally { setCloning(false) }
  }

  async function handleBrandingApply(brandingJson: string | null) {
    if (!proposal) return
    try {
      await api.patch(`/proposals/${slug}`, { branding_override: brandingJson })
      setProposal({ ...proposal, branding_override: brandingJson } as ProposalWithDetails)
    } catch { /* */ }
    setShowBranding(false)
  }

  async function handleAddArea() {
    if (!newAreaName.trim() || !proposal) return
    setSavingArea(true)
    try {
      const area = await api.post<ProposalArea>(`/proposals/${slug}/areas`, {
        name: newAreaName.trim(), sort_order: (proposal.areas ?? []).length,
      })
      setProposal(prev => prev ? { ...prev, areas: [...(prev.areas ?? []), area] } : prev)
      setNewAreaName('')
      setAddingArea(false)
    } catch { /* */ }
    finally { setSavingArea(false) }
  }

  async function handleAddItem(areaId: string, item: {
    name: string; item_type: string; quantity: number; cost: number; sell_price: number
    description?: string; unit?: string; manufacturer?: string; mpn?: string
    msrp?: number; catalog_item_id?: string; image_r2_key?: string
  }) {
    if (!proposal) return
    try {
      const existing = (proposal.line_items ?? []).filter(i => i.area_id === areaId)
      const newItem = await api.post<ProposalLineItem>(`/proposals/${slug}/items`, {
        area_id: areaId, item_type: item.item_type, name: item.name,
        description: item.description || '', quantity: item.quantity, unit: item.unit || 'ea',
        cost: item.cost, sell_price: item.sell_price, msrp: item.msrp,
        manufacturer: item.manufacturer, mpn: item.mpn,
        catalog_item_id: item.catalog_item_id, image_r2_key: item.image_r2_key,
        sort_order: existing.length,
      })
      setProposal(prev => prev ? {
        ...prev, line_items: [...(prev.line_items ?? []), newItem],
        subtotal: prev.subtotal + item.sell_price * item.quantity,
        total: prev.total + item.sell_price * item.quantity,
      } : prev)
    } catch { /* */ }
  }

  async function _handleDeleteItem(itemSlug: string, itemId: string) {
    const item = (proposal?.line_items ?? []).find(i => i.id === itemId)
    try {
      await api.delete(`/proposals/${slug}/items/${itemSlug}`)
      setProposal(prev => prev ? {
        ...prev, line_items: (prev.line_items ?? []).filter(i => i.id !== itemId),
        subtotal: prev.subtotal - (item ? item.sell_price * item.quantity : 0),
        total: prev.total - (item ? item.sell_price * item.quantity : 0),
      } : prev)
    } catch { /* */ }
  }

  async function _handleFieldSave(field: string, value: string) {
    try {
      await api.patch(`/proposals/${slug}`, { [field]: value })
      setProposal(prev => prev ? { ...prev, [field]: value } : prev)
    } catch { /* */ }
  }

  async function refreshProposal() {
    if (!slug) return
    try {
      const data = await api.get<ProposalWithDetails>(`/proposals/${slug}`)
      setProposal(data)
    } catch { /* */ }
  }

  async function handleToggleTaxExempt(item: ProposalLineItem) {
    const newVal = item.is_tax_exempt === 1 ? 0 : 1
    setActiveItem(null)
    // Optimistic update
    setProposal(prev => prev ? {
      ...prev,
      line_items: (prev.line_items ?? []).map(i => i.id === item.id ? { ...i, is_tax_exempt: newVal } : i),
    } : prev)
    try {
      await api.patch(`/proposals/${slug}/items/${item.slug}`, { is_tax_exempt: newVal })
      await refreshProposal()
    } catch (err) {
      // Revert
      setProposal(prev => prev ? {
        ...prev,
        line_items: (prev.line_items ?? []).map(i => i.id === item.id ? { ...i, is_tax_exempt: item.is_tax_exempt } : i),
      } : prev)
      toastError('Failed to update tax exempt status', err)
    }
  }

  async function handleDeleteItem(item: ProposalLineItem) {
    setActiveItem(null)
    try {
      await api.delete(`/proposals/${slug}/items/${item.slug}`)
      setProposal(prev => prev ? {
        ...prev,
        line_items: (prev.line_items ?? []).filter(i => i.id !== item.id),
        subtotal: prev.subtotal - item.sell_price * item.quantity,
        total: prev.total - item.sell_price * item.quantity,
      } : prev)
    } catch (err) {
      toastError('Failed to delete item', err)
    }
  }

  async function handleMoveItem(item: ProposalLineItem, direction: 'up' | 'down') {
    if (!proposal) return
    const areaItems = [...(proposal.line_items ?? [])].filter(i => i.area_id === item.area_id).sort((a, b) => a.sort_order - b.sort_order)
    const idx = areaItems.findIndex(i => i.id === item.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= areaItems.length) return
    const reordered = [
      { id: areaItems[idx].id, sort_order: areaItems[swapIdx].sort_order, parent_item_id: areaItems[idx].parent_item_id },
      { id: areaItems[swapIdx].id, sort_order: areaItems[idx].sort_order, parent_item_id: areaItems[swapIdx].parent_item_id },
    ]
    // Optimistic update
    setProposal(prev => {
      if (!prev) return prev
      const orderMap = new Map(reordered.map(r => [r.id, r.sort_order]))
      return { ...prev, line_items: (prev.line_items ?? []).map(i => orderMap.has(i.id) ? { ...i, sort_order: orderMap.get(i.id)! } : i) }
    })
    setActiveItem(null)
    try {
      await api.patch(`/proposals/${slug}/items-reorder`, { items: reordered })
    } catch {
      await refreshProposal()
    }
  }

  // ─── Render Guards ───
  if (loading) return <LoadingSpinner />
  if (error || !proposal) return <div style={{ color: 'var(--text-tertiary)', padding: 24 }}>{error || 'Not found'}</div>

  // ─── Computed ───
  const sortedAreas = [...(proposal.areas ?? [])].sort((a, b) => a.sort_order - b.sort_order)
  const allItems = proposal.line_items ?? []
  const laborTotal = allItems.filter(i => i.item_type === 'labor' && !i.is_optional).reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const equipTotal = allItems.filter(i => i.item_type !== 'labor' && !i.is_optional).reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const taxExemptTotal = allItems.filter(i => i.is_tax_exempt === 1 && !i.is_optional).reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const totalCost = allItems.filter(i => !i.is_optional).reduce((s, i) => s + i.cost * i.quantity, 0)
  const totalSell = allItems.filter(i => !i.is_optional).reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const profit = totalSell - totalCost
  const marginPct = totalSell > 0 ? ((totalSell - totalCost) / totalSell) * 100 : 0
  const st = STATUS_MAP[proposal.status] || STATUS_MAP.draft

  // Margin alerts
  const lowMarginItems = allItems.filter((i) => {
    if (i.is_optional || i.sell_price <= 0) return false
    const m = ((i.sell_price - i.cost) / i.sell_price) * 100
    return m < marginAlertThreshold && m > 0
  })
  const belowCostItems = allItems.filter((i) => {
    if (i.is_optional || i.sell_price <= 0) return false
    return i.sell_price <= i.cost
  })

  const getAreaItems = (areaId: string) => allItems.filter(i => i.area_id === areaId)

  // ─── Sub-Views ───
  if (view === V.AREA && activeArea) {
    const areaItems = getAreaItems(activeArea.id)
    const areaTotal = areaItems.reduce((s, i) => s + i.sell_price * i.quantity, 0)
    return (
      <div style={S.page}>
        <div style={S.header}><div style={S.headerRow}>
          <button style={S.headerBtn} onClick={back}>{I.back}</button>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{activeArea.name}</span>
          <div style={{ width: 28 }} />
        </div></div>
        <div style={{ padding: 16, paddingBottom: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary)', fontWeight: 600 }}>{areaItems.length} line item{areaItems.length !== 1 ? 's' : ''}</span>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Area Total</div>
              <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>${fmt(areaTotal)}</div>
            </div>
          </div>
          {areaItems.map(item => {
            const itemMargin = item.sell_price > 0 ? ((1 - item.cost / item.sell_price) * 100) : 0
            const mColor = itemMargin <= 0 ? 'var(--err)' : itemMargin < marginAlertThreshold ? 'var(--warn)' : 'var(--ok)'
            return (
              <div key={item.id} onClick={() => setActiveItem(item)} style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: 16, marginBottom: 8, border: '1px solid var(--border-default)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <BrandBadge brand={item.manufacturer || ''} imageKey={item.image_r2_key} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
                      {item.manufacturer || 'Item'}
                      {item.item_type === 'labor' && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'var(--info-m)', color: 'var(--info)' }}>LABOR</span>}
                      {item.is_tax_exempt === 1 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'var(--bg-base)', color: 'var(--text-secondary)' }}>Tax Exempt</span>}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginTop: 1 }}>{item.name}</div>
                    {item.description && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>{item.description}</div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                  <DataCell label="Cost" value={`$${fmt(item.cost)}`} muted />
                  <DataCell label="Price" value={`$${fmt(item.sell_price)}`} />
                  <DataCell label="Qty" value={String(item.quantity)} />
                  <DataCell label="Total" value={`$${fmt(item.sell_price * item.quantity)}`} bold />
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-tertiary)' }}>Margin</span>
                    <span style={{ fontWeight: 700, color: mColor, fontFamily: 'var(--font-mono)' }}>{itemMargin.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-elevated)' }}>
                    <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(itemMargin, 100)}%`, background: `linear-gradient(90deg, ${mColor}, ${mColor}dd)`, transition: 'width 0.3s' }} />
                  </div>
                </div>
              </div>
            )
          })}
          <button style={S.addBtn} onClick={() => setCatalogAreaId(activeArea.id)}>{I.plus} <span>Add Line Item</span></button>
        </div>
        {catalogAreaId && (
          <CatalogPickerModal
            isOpen={!!catalogAreaId}
            onClose={() => setCatalogAreaId(null)}
            onSelect={async (catalogItem) => {
              setCatalogAreaId(null)
              await handleAddItem(activeArea.id, {
                name: catalogItem.custom_name || catalogItem.name || 'Unknown', item_type: 'product', quantity: 1,
                cost: catalogItem.cost, sell_price: catalogItem.sell_price,
                description: catalogItem.custom_description || catalogItem.description || '', unit: 'ea',
                manufacturer: catalogItem.custom_brand || catalogItem.brand || undefined, mpn: catalogItem.custom_mpn || catalogItem.mpn || undefined,
                msrp: catalogItem.msrp, catalog_item_id: catalogItem.id,
                image_r2_key: catalogItem.custom_image_r2_key || catalogItem.image_r2_key || undefined,
              })
            }}
            onCustomItem={() => setCatalogAreaId(null)}
          />
        )}
        {/* Item Action Sheet */}
        {activeItem && (
          <>
            <div style={S.backdrop} onClick={() => setActiveItem(null)} />
            <div style={S.actionSheet}>
              <div style={S.actionSheetHandle} />
              <div style={{ padding: '4px 20px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{activeItem.manufacturer || 'Item'}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{activeItem.name}</div>
              </div>
              <ActionItem
                icon={activeItem.is_tax_exempt === 1
                  ? <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  : <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>}
                label={activeItem.is_tax_exempt === 1 ? 'Remove Tax Exempt' : 'Mark Tax Exempt'}
                desc={activeItem.is_tax_exempt === 1 ? 'Re-apply tax to this item' : 'Exclude from tax calculation'}
                onClick={() => handleToggleTaxExempt(activeItem)}
              />
              <ActionItem icon={I.edit} label="Move Up" desc="Move item up in order" onClick={() => handleMoveItem(activeItem, 'up')} />
              <ActionItem icon={I.edit} label="Move Down" desc="Move item down in order" onClick={() => handleMoveItem(activeItem, 'down')} />
              <div style={{ height: 1, background: 'var(--border-subtle)', margin: '8px 0' }} />
              <ActionItem icon={I.trash} label="Remove Item" desc="Delete from proposal" danger onClick={() => {
                if (confirm(`Remove "${activeItem.name}"?`)) handleDeleteItem(activeItem)
                else setActiveItem(null)
              }} />
            </div>
          </>
        )}
      </div>
    )
  }

  if (view === V.FINANCIAL) return (
    <div style={S.page}>
      <div style={S.header}><div style={S.headerRow}>
        <button style={S.headerBtn} onClick={back}>{I.back}</button>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Breakdown</span>
        <div style={{ width: 28 }} />
      </div></div>
      <div style={{ padding: 16, paddingBottom: 40 }}>
        <Card>
          <FinRow label="Parts (Taxable)" value={`$${fmt(equipTotal - taxExemptTotal)}`} />
          {taxExemptTotal > 0 && (
            <>
              <Sep />
              <FinRow label="Parts (Tax Exempt)" value={`$${fmt(taxExemptTotal)}`} />
            </>
          )}
          <Sep />
          <FinRow label="Total Parts" value={`$${fmt(equipTotal)}`} bold />
          <Sep />
          <FinRow label="Labor" value={`$${fmt(laborTotal)}`} />
          <Sep />
          <FinRow label="Subtotal" value={`$${fmt(proposal.subtotal)}`} bold />
          <Sep />
          <FinRow label={`Sales Tax (${((proposal.tax_rate || 0) * 100).toFixed(2)}%)`} value={`$${fmt(proposal.tax_amount)}`} />
          <div style={{ padding: 16, borderTop: '2px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>Total</span>
            <span style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>${fmt(proposal.total)}</span>
          </div>
        </Card>
      </div>
    </div>
  )

  if (view === V.PROFIT) return (
    <div style={S.page}>
      <div style={S.header}><div style={S.headerRow}>
        <button style={S.headerBtn} onClick={back}>{I.back}</button>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Margin Analysis</span>
        <div style={{ width: 28 }} />
      </div></div>
      <div style={{ padding: 16, paddingBottom: 40 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
          <Stat label="Your Cost" value={`$${fmt(totalCost)}`} />
          <Stat label="Client Pays" value={`$${fmt(proposal.total)}`} />
          <Stat label="Net Profit" value={`$${fmt(profit)}`} color="#059669" />
          <Stat label="Margin" value={`${marginPct.toFixed(1)}%`} color="#059669" />
        </div>
        <SectionLabel>Per Area</SectionLabel>
        <Card>
          {sortedAreas.map((a, i) => {
            const items = getAreaItems(a.id)
            const aTotal = items.reduce((s, it) => s + it.sell_price * it.quantity, 0)
            const aCost = items.reduce((s, it) => s + it.cost * it.quantity, 0)
            const m = aTotal > 0 ? ((1 - aCost / aTotal) * 100).toFixed(1) : '0.0'
            return (
              <div key={a.id}>
                {i > 0 && <Sep />}
                <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{a.name}</span>
                  <div style={{ display: 'flex', gap: 14 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>${fmt(aTotal)}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: Number(m) > 40 ? 'var(--ok)' : 'var(--warn)' }}>{m}%</span>
                  </div>
                </div>
              </div>
            )
          })}
        </Card>
      </div>
    </div>
  )

  if (view === V.PAYMENTS) return (
    <div style={S.page}>
      <div style={S.header}><div style={S.headerRow}>
        <button style={S.headerBtn} onClick={back}>{I.back}</button>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Payment Schedule</span>
        <div style={{ width: 28 }} />
      </div></div>
      <div style={{ padding: 16, paddingBottom: 40 }}>
        {(proposal.payments ?? []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>No milestones set</div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 4 }}>Define when payments are due</div>
          </div>
        ) : (
          <Card>
            {(proposal.payments ?? []).map((p, i) => (
              <div key={p.id}>
                {i > 0 && <Sep />}
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{p.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>${fmt(p.calculated_amount ?? p.amount_value)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>
                    {p.amount_type === 'percent' ? `${p.amount_value}%` : p.amount_type === 'balance' ? 'Balance' : `$${fmt(p.amount_value)}`}
                    {p.trigger_type && ` · ${p.trigger_type}`}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  )

  if (view === V.ACTIVITY) return (
    <div style={S.page}>
      <div style={S.header}><div style={S.headerRow}>
        <button style={S.headerBtn} onClick={back}>{I.back}</button>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Activity Log</span>
        <div style={{ width: 28 }} />
      </div></div>
      <div style={{ padding: 16, paddingBottom: 40 }}>
        <Card>
          {activity.map((a, i) => (
            <div key={a.id}>
              {i > 0 && <Sep />}
              <div style={{ padding: '12px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{a.detail || a.action}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{timeAgo(a.performed_at)}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  )

  // ─── Main View ───
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerRow}>
          <button style={S.headerBtn} onClick={() => navigate('/proposals')}>{I.back}</button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em' }}>
              P-{proposal.slug?.slice(-3) || proposal.id.slice(-3)}
            </div>
          </div>
          <button style={S.headerBtn} onClick={() => setShowActions(!showActions)}>{I.more}</button>
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid var(--border-h)' }}>
          {['Build', 'Info'].map((l, i) => (
            <button key={l} onClick={() => setTab(i)} style={{
              ...S.tab, fontWeight: tab === i ? 700 : 450,
              color: tab === i ? '#fff' : 'var(--text-3)',
              borderBottom: tab === i ? '2px solid var(--accent)' : '2px solid transparent',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Action Sheet */}
      {showActions && (
        <>
          <div style={S.backdrop} onClick={() => setShowActions(false)} />
          <div style={S.actionSheet}>
            <div style={S.actionSheetHandle} />
            <ActionItem icon={I.send} label="Send to Client" desc="Email proposal link" accent onClick={() => handleStatusChange('sent')} />
            <ActionItem icon={I.eye} label="Preview" desc="See client's view" onClick={() => {
              setShowActions(false)
              if (shareUrl) window.open(shareUrl, '_blank')
              else generateToken()
            }} />
            <ActionItem icon={I.copy} label="Copy Client Link" desc="Share proposal URL" onClick={() => {
              setShowActions(false)
              if (shareUrl) navigator.clipboard.writeText(shareUrl)
              else generateToken()
            }} />
            <div style={{ height: 1, background: 'var(--border-subtle)', margin: '8px 0' }} />
            <ActionItem icon={I.pdf} label="Export PDF" desc="Save the client view as a PDF" onClick={downloadPdf} />
            <ActionItem icon={I.clone} label="Duplicate" desc="Clone this proposal" onClick={handleClone} />
            <ActionItem icon={I.settings} label="Proposal Settings" desc="Tax rate, expiry, visibility" onClick={() => setShowActions(false)} />
            <ActionItem icon={I.edit} label="Client Branding" desc="Override branding for this proposal" onClick={() => { setShowActions(false); setShowBranding(true) }} />
            <div style={{ height: 1, background: 'var(--border-subtle)', margin: '8px 0' }} />
            <ActionItem icon={I.archive} label="Archive" desc="Remove from active list" muted onClick={() => handleStatusChange('expired')} />
            <ActionItem icon={I.trash} label="Delete" desc="Permanently remove" danger onClick={() => setShowActions(false)} />
          </div>
        </>
      )}

      {/* Content */}
      <div style={{ paddingBottom: 160 }}>
        {tab === 0 ? (
          /* ─── Build Tab ─── */
          <div style={{ padding: '12px 16px' }}>
            <SectionLabel>Proposal Sections</SectionLabel>
            <Card>
              <Row label="Cover Image" badge={!!proposal.cover_image_r2_key} onClick={() => {}} />
              <Sep />
              <Row label="Company Info" badge={!!proposal.about_us} onClick={() => {}} />
              <Sep />
              <Row label="Scope of Work" badge={!!proposal.project_description} onClick={() => {}} />
            </Card>

            {/* Margin Warning */}
            {marginProposalWarningEnabled && (lowMarginItems.length > 0 || belowCostItems.length > 0) && (
              <div style={{
                padding: '10px 14px', marginTop: 20, marginBottom: 8,
                background: belowCostItems.length > 0 ? 'var(--err-m)' : 'var(--warn-m)',
                border: `1px solid ${belowCostItems.length > 0 ? 'var(--err)' : 'var(--warn)'}`,
                borderRadius: 12, fontSize: 13,
                color: belowCostItems.length > 0 ? 'var(--err)' : 'var(--warn)',
              }}>
                ⚠️{' '}
                {lowMarginItems.length > 0 && <>{lowMarginItems.length} item{lowMarginItems.length !== 1 ? 's' : ''} below {marginAlertThreshold}% margin</>}
                {lowMarginItems.length > 0 && belowCostItems.length > 0 && ' · '}
                {belowCostItems.length > 0 && <strong>{belowCostItems.length} item{belowCostItems.length !== 1 ? 's' : ''} below cost</strong>}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, marginBottom: 10 }}>
              <SectionLabel style={{ margin: 0 }}>Areas & Line Items</SectionLabel>
              <button style={{ ...S.iconBtn, color: 'var(--accent)' }} onClick={() => setAddingArea(true)}>{I.plus}</button>
            </div>

            {sortedAreas.map(a => {
              const items = getAreaItems(a.id)
              const aTotal = items.reduce((s, i) => s + i.sell_price * i.quantity, 0)
              return (
                <div key={a.id} onClick={() => go(V.AREA, a)} style={S.areaCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={S.areaDot} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 1 }}>{items.length} line item{items.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>${fmt(aTotal)}</span>
                    {I.chev}
                  </div>
                </div>
              )
            })}

            {addingArea ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input
                  value={newAreaName}
                  onChange={e => setNewAreaName(e.target.value)}
                  placeholder="Area name..."
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleAddArea(); if (e.key === 'Escape') { setAddingArea(false); setNewAreaName('') } }}
                  style={{ flex: 1, padding: '10px 12px', fontSize: 16, borderRadius: 10, border: '1px solid var(--border-default)', outline: 'none', fontFamily: 'inherit', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                />
                <button onClick={handleAddArea} disabled={savingArea} style={{ ...S.addBtn, width: 'auto', marginTop: 0, padding: '10px 16px' }}>
                  {savingArea ? '...' : 'Add'}
                </button>
              </div>
            ) : (
              <button style={S.addBtn} onClick={() => setAddingArea(true)}>{I.plus} <span>Add Area</span></button>
            )}

            <SectionLabel style={{ marginTop: 28 }}>Financials</SectionLabel>
            <Card>
              <Row label="Breakdown" right={<Mono>${fmt(proposal.total)}</Mono>} onClick={() => go(V.FINANCIAL)} />
              <Sep />
              <Row label="Payment Schedule" right={<span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{(proposal.payments ?? []).length} milestones</span>} onClick={() => go(V.PAYMENTS)} />
              <Sep />
              <Row label="Margin Analysis" right={<span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: marginPct > 40 ? 'var(--ok)' : 'var(--warn)' }}>{marginPct.toFixed(1)}%</span>} onClick={() => go(V.PROFIT)} />
            </Card>

            <SectionLabel style={{ marginTop: 28 }}>Terms & Conditions</SectionLabel>
            <Card>
              <Row label="Project Terms" badge={!!proposal.client_notes} onClick={() => {}} />
            </Card>
          </div>
        ) : (
          /* ─── Info Tab ─── */
          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, background: st.bg, marginBottom: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: 4, background: st.dot }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: st.text }}>{st.label}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 20 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                {proposal.title}
              </h1>
            </div>

            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>Assigned To</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{(proposal as unknown as Record<string, unknown>).assigned_to_name as string || 'Unassigned'}</span>
              </div>
              <Sep />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>Valid Until</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{proposal.expires_at ? fmtDate(proposal.expires_at) : 'No Expiry'}</span>
              </div>
              <Sep />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>Created</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>{fmtDate(proposal.created_at)}</span>
              </div>
            </Card>

            <SectionLabel style={{ marginTop: 24 }}>Client</SectionLabel>
            {client ? (
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 14, padding: 16, border: '1px solid var(--border-default)' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{client.contact_name || client.company_name}</div>
                {client.company_name && client.contact_name && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{client.company_name}</div>}
                <div style={{ height: 1, background: 'var(--border-subtle)', margin: '12px 0' }} />
                {client.email && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{client.email}</div>}
                {client.phone && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{client.phone}</div>}
              </div>
            ) : (
              <Card><div style={{ padding: '16px', color: 'var(--text-tertiary)', fontSize: 13 }}>No client assigned</div></Card>
            )}

            {(client?.address_line1 || client?.city) && (
              <>
                <SectionLabel style={{ marginTop: 20 }}>Project Location</SectionLabel>
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 14, padding: 16, border: '1px solid var(--border-default)' }}>
                  {client.address_line1 && <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{client.address_line1}</div>}
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {[client.city, client.state, client.zip].filter(Boolean).join(', ')}
                  </div>
                </div>
              </>
            )}

            {vmsRules.length > 0 && (
              <>
                <SectionLabel style={{ marginTop: 24 }}>VMS Licensing</SectionLabel>
                <Card>
                  <div style={{ padding: '12px 16px' }}>
                    <select
                      value={(proposal as unknown as Record<string, unknown>).vms_type as string || ''}
                      onChange={(e) => handleVmsTypeChange(e.target.value || null)}
                      disabled={vmsUpdating}
                      style={{ width: '100%', padding: '10px 12px', fontSize: 15, borderRadius: 10, border: '1px solid var(--border-default)', fontFamily: 'inherit', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                    >
                      <option value="">None</option>
                      {vmsRules.map(r => (
                        <option key={r.vms_type} value={r.vms_type}>{r.vms_display_name}</option>
                      ))}
                    </select>
                    {vmsUpdating && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>Recalculating...</div>}
                  </div>
                </Card>
              </>
            )}

            <SectionLabel style={{ marginTop: 24 }}>Recent Activity</SectionLabel>
            <Card>
              {activity.slice(0, 3).map((a, i) => (
                <div key={a.id}>
                  {i > 0 && <Sep />}
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.detail || a.action}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>{I.clock} {timeAgo(a.performed_at)}</div>
                  </div>
                </div>
              ))}
              {activity.length > 3 && (
                <>
                  <Sep />
                  <button onClick={() => go(V.ACTIVITY)} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--primary)', textAlign: 'center', fontFamily: 'inherit' }}>
                    View Full History
                  </button>
                </>
              )}
            </Card>

            <SectionLabel style={{ marginTop: 24 }}>Client Access</SectionLabel>
            <Card>
              <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>Share this link with your client:</div>
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', background: 'var(--bg-base)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-all' as const }}>
                  {shareUrl || 'Not generated yet'}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button style={{ ...S.smallBtn, flex: 1 }} onClick={() => {
                    if (shareUrl) navigator.clipboard.writeText(shareUrl)
                    else generateToken()
                  }}>{I.copy} Copy Link</button>
                  <button style={{ ...S.smallBtn, flex: 1 }} onClick={() => {
                    if (!shareUrl) generateToken()
                  }}>{I.send} Email</button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div style={S.bottomBar}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Proposal Total</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--accent)', letterSpacing: '-0.02em' }}>
              ${fmt(proposal.total)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: marginPct > 40 ? 'var(--ok)' : 'var(--warn)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: marginPct > 40 ? 'var(--ok)' : 'var(--warn)' }}>
              {marginPct.toFixed(1)}% margin
            </span>
          </div>
        </div>
        <button style={S.primaryBtn} onClick={() => handleStatusChange('sent')}>
          {I.send}
          <span>Send to Client</span>
        </button>
      </div>

      {/* Branding Override Modal */}
      {showBranding && (
        <BrandingOverrideModal
          currentOverride={(proposal as unknown as Record<string, unknown>)?.branding_override as string || null}
          onApply={handleBrandingApply}
          onClose={() => setShowBranding(false)}
        />
      )}
    </div>
  )
}

// ─── Shared Components ───

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border-default)', overflow: 'hidden', ...style }}>{children}</div>
}

function Row({ label, right, badge, onClick }: { label: string; right?: React.ReactNode; badge?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={S.row}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
        {badge && <div style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--ok)' }} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{right}{I.chev}</div>
    </button>
  )
}

function Sep() { return <div style={{ height: 1, background: 'var(--border-subtle)', marginLeft: 16, marginRight: 16 }} /> }

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, ...style }}>{children}</div>
}

function Mono({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <span style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', ...style }}>{children}</span>
}

function FinRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 16px' }}>
      <span style={{ fontSize: 14, color: bold ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: bold ? 700 : 500, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border-default)', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-mono)', color: color || 'var(--text-primary)', marginTop: 4 }}>{value}</div>
    </div>
  )
}

function DataCell({ label, value, muted, bold }: { label: string; value: string; muted?: boolean; bold?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: bold ? 800 : 600, color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)', marginTop: 2 }}>{value}</div>
    </div>
  )
}

function BrandBadge({ brand, imageKey, size = 42 }: { brand: string; imageKey?: string | null; size?: number }) {
  if (imageKey) {
    return <img src={`/api/files/${imageKey}`} alt="" style={{ width: size, height: size, borderRadius: size * 0.24, objectFit: 'cover', flexShrink: 0 }} />
  }
  const bg = getBrandColor(brand)
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.24, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.26, fontWeight: 800, letterSpacing: '-0.02em',
      textTransform: 'uppercase', flexShrink: 0,
    }}>
      {(brand || '?').slice(0, 3)}
    </div>
  )
}

function ActionItem({ icon, label, desc, accent, muted, danger, onClick }: {
  icon: React.ReactNode; label: string; desc?: string; accent?: boolean; muted?: boolean; danger?: boolean; onClick?: () => void
}) {
  const color = danger ? 'var(--err)' : accent ? 'var(--accent)' : muted ? 'var(--text-tertiary)' : 'var(--text-primary)'
  return (
    <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
      <span style={{ color, opacity: 0.7 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{desc}</div>}
      </div>
    </button>
  )
}

// ─── Styles ───

const S: Record<string, React.CSSProperties> = {
  page: {
    background: 'var(--bg-base)', minHeight: '100dvh',
    fontFamily: 'var(--font)', position: 'relative',
  },
  header: {
    background: 'var(--bg-3)', color: '#fff',
    position: 'sticky', top: 0, zIndex: 30,
    paddingTop: 'env(safe-area-inset-top, 0px)' as string,
  },
  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 14px',
  },
  headerBtn: {
    background: 'none', border: 'none', color: '#fff', padding: 6,
    cursor: 'pointer', borderRadius: 8, display: 'flex', alignItems: 'center',
  },
  tab: {
    flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer',
    fontSize: 13, background: 'transparent', fontFamily: 'inherit',
  },
  bottomBar: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'var(--bg-surface)', borderTop: '1px solid var(--border-default)',
    padding: '12px 16px',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' as string,
    zIndex: 25, boxShadow: '0 -4px 20px rgba(0,0,0,0.25)',
  },
  primaryBtn: {
    width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
    background: 'linear-gradient(135deg, var(--accent), var(--accent))', color: '#fff',
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: '0 4px 14px var(--accent-m)', fontFamily: 'inherit',
  },
  row: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
    textAlign: 'left' as const, fontFamily: 'inherit',
  },
  areaCard: {
    background: 'var(--bg-surface)', borderRadius: 14, padding: '14px 16px', marginBottom: 6,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    border: '1px solid var(--border-default)', gap: 12,
  },
  areaDot: {
    width: 4, height: 32, borderRadius: 2, background: 'var(--accent)', flexShrink: 0,
  },
  addBtn: {
    width: '100%', padding: '13px', borderRadius: 12,
    border: '1.5px dashed var(--accent-m)', background: 'transparent',
    color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    marginTop: 6, fontFamily: 'inherit',
  },
  smallBtn: {
    padding: '8px 0', borderRadius: 8, border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    fontFamily: 'inherit',
  },
  iconBtn: {
    width: 34, height: 34, borderRadius: 10, border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0,
  },
  backdrop: {
    position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.4)',
    zIndex: 40, backdropFilter: 'blur(2px)',
  },
  actionSheet: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'var(--bg-surface)', borderRadius: '20px 20px 0 0', zIndex: 50,
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' as string,
    paddingTop: 8, boxShadow: '0 -8px 40px rgba(0,0,0,0.35)',
    maxHeight: '80vh', overflowY: 'auto' as const,
  },
  actionSheetHandle: {
    width: 36, height: 4, borderRadius: 2, background: 'var(--border-default)',
    margin: '0 auto 12px',
  },
}
